import { useEffect, useRef } from 'react';
import AwesomeFactsPanel from './AwesomeFactsPanel';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type YTPlayer = any;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export default function HeroSection() {
  const playerRef = useRef<YTPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleExplore = () => {
    const el = document.querySelector('#services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const initPlayer = () => {
      if (!containerRef.current) return;
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: 'ixj_ODXS0SI',
        playerVars: {
          autoplay: 1,
          mute: 1,
          start: 2,
          controls: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          showinfo: 0,
          disablekb: 1,
          fs: 0,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        events: {
          onStateChange: (event: { data: number }) => {
            // State 0 = ended — seek back to 2s and keep playing
            if (event.data === 0) {
              playerRef.current?.seekTo(2, true);
              playerRef.current?.playVideo();
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      if (!document.getElementById('yt-api-script')) {
        const script = document.createElement('script');
        script.id = 'yt-api-script';
        script.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(script);
      }
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      playerRef.current?.destroy();
    };
  }, []);

  return (
    <section className="bg-black text-white flex flex-col pt-24">
      {/* ── Top: Giant headline + right description ── */}
      <div className="w-full px-6 lg:px-10 xl:px-16 pt-12 lg:pt-16 pb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 max-w-[1600px] mx-auto">

        {/* Massive headline */}
        <div className="flex-1">
          <p className="text-xs tracking-[0.25em] text-orange-500 uppercase mb-5">
            Serving All of New Jersey &amp; Pennsylvania
          </p>
          <h1
            className="font-display font-black leading-[0.88] tracking-tight"
            style={{ fontSize: 'clamp(50px, 7.5vw, 106px)' }}
          >
            <span className="block text-white">NJ &amp; PA&apos;s</span>
            <span className="block text-orange-500">Trusted</span>
            <span className="block text-white">Flat Roofing</span>
          </h1>
        </div>

        {/* Right: description + CTAs + certifications */}
        <div className="lg:max-w-[340px] flex-shrink-0 lg:pb-2">
          <p className="text-gray-400 text-sm leading-relaxed mb-7">
            Certified commercial flat roofing specialists with a licensed engineer on staff.
            Clean, fast, and courteous crews serving every county in New Jersey and Pennsylvania.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <a
              href="tel:8566776191"
              className="px-7 py-3.5 bg-orange-600 text-white text-sm font-semibold hover:bg-orange-500 transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              Call Us Now
            </a>
            <button
              onClick={handleExplore}
              className="px-7 py-3.5 border border-white/30 text-white text-sm hover:border-white hover:bg-white/5 transition-colors cursor-pointer whitespace-nowrap"
            >
              Our Services
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-600 uppercase tracking-widest">Certified:</span>
            {['GAF', 'Tamko', 'Duralast', 'Mulehide', 'OC'].map((b) => (
              <span key={b} className="px-2.5 py-1 border border-white/10 text-xs text-gray-500 whitespace-nowrap">
                {b}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom: Form card + Hero video ── */}
      <div className="flex flex-col lg:flex-row min-h-[620px] lg:min-h-[680px]">

        {/* Awesome Facts panel */}
        <div className="w-full lg:w-[480px] xl:w-[540px] flex-shrink-0">
          <AwesomeFactsPanel />
        </div>

        {/* Hero video — YouTube IFrame API for reliable looping */}
        <div className="relative flex-1 min-h-[360px] overflow-hidden bg-black">
          {/* The YT API replaces this div with an iframe */}
          <div
            ref={containerRef}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{
              width: 'max(100%, calc(100vh * 1.7778))',
              height: 'max(100%, calc(100vw * 0.5625))',
              minWidth: '100%',
              minHeight: '100%',
            }}
          />
          <div className="absolute inset-0 bg-black/30" />

          {/* Experience badge */}
          <div className="absolute bottom-8 left-8 bg-orange-600 text-white px-7 py-5 z-10">
            <p className="text-4xl font-display font-black leading-none">20+</p>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase mt-2 text-orange-100">
              Years of Experience
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
